import React, { useState, useEffect, useRef, forwardRef } from "react";

import { useHistory, useLocation } from "react-router-dom";

import {
  Typography,
  Grid,
  Button,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Slide,
  Dialog,
  CircularProgress,
  Divider,
  Box,
} from "@mui/material";

import { apiGet } from "@services/API";
import moment from "moment";

import Observations from "@components/patients/Observations";
import Assessments from "@components/patients/Assessments";

import Notes from "@components/patients/Notes";
import EpisodesOfCare from "@components/patients/EpisodesOfCare";
import Report from "@components/patients/Report";
import DischargeReport from "@components/patients/DischargeReport";

import CloseIcon from "@mui/icons-material/Close";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function headers() {
  let headers = {};

  let token = localStorage.getItem("covid19_token");
  let mfa_token = localStorage.getItem("mfa_token");

  if (token && token.length > 0 && mfa_token && mfa_token.length > 0) {
    headers = { "x-access-token": token };
  }

  return headers;
}

export default function Patient({
  isVisible,
  onCloseScreen,
  patientId,
  ...props
}) {
  let history = useHistory();

  const isValidPatientId = patientId && !isNaN(patientId);

  const [loading, setLoading] = useState(true);

  const [dataPatient, setDataPatient] = useState(null);
  const [dataPatientLatestNotes, setDataPatientLatestNotes] = useState(null);
  const [dataPatientObservations, setDataPatientObservations] = useState(null);
  const [dataPatientAssessments, setDataPatientAssessments] = useState(null);

  const [dataPatientEOC, setDataPatientEOC] = useState(null);

  const [isReportVisible, setIsReportVisible] = useState(false);
  const [isDischargeReportVisible, setIsDischargeReportVisible] =
    useState(false);

  useEffect(() => {
    if (isValidPatientId) {
      getPatientObservations(patientId);
      getPatientAssessments(patientId);
      getPatientEOC(patientId);
      getPatientDetailsFull(patientId);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [
        patientDetailsResponse,
        episodesOfCareResponse,
        observationsResponse,
        assessmentsResponse,
      ] = await Promise.all([
        apiGet(
          `/patientDetailsFull/${patientId}`,
          {
            headers: headers(),
          },
          history
        ),
        apiGet(
          `/eposides_of_care/${patientId}`,
          {
            headers: headers(),
          },
          history
        ),
        apiGet(
          `/observations/${patientId}/0`,
          {
            headers: headers(),
          },
          history
        ),
        apiGet(
          `/assessments/${patientId}/all/0`,
          {
            headers: headers(),
          },
          history
        ),
      ]);

      setDataPatient(patientDetailsResponse.data.patientDetails[0]);
      setDataPatientLatestNotes(patientDetailsResponse.data.mostRecentNotes);

      if (episodesOfCareResponse.data.length > 0) {
        setDataPatientEOC(episodesOfCareResponse.data);
      } else {
        setDataPatientEOC(null);
      }

      if (observationsResponse.data.length > 0) {
        const sortedData = observationsResponse.data.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );

        setDataPatientObservations(sortedData);
      } else {
        setDataPatientObservations(null);
      }

      if (assessmentsResponse.data.length > 0) {
        const sortedData = assessmentsResponse.data.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
        setDataPatientAssessments(sortedData);
      } else {
        setDataPatientAssessments(null);
      }

      setLoading(false);
    } catch (error) {
      console.error("Patient - fetchData error: ", error);
      setDataPatient(null);
      setDataPatientLatestNotes(null);
      setDataPatientEOC(null);
      setDataPatientObservations(null);
      setDataPatientAssessments(null);
      setLoading(false);
      history.push("/login");
    }
  };

  const getPatientDetailsFull = async (patientId) => {
    await apiGet(
      `/patientDetailsFull/${patientId}`,
      { headers: headers() },
      history
    )
      .then((response) => {
        if (!isNullOrEmpty(response.data)) {
          setDataPatient(response.data.patientDetails[0]);
          setDataPatientLatestNotes(response.data.mostRecentNotes);
        } else {
          setDataPatient(null);
          setDataPatientLatestNotes(null);
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setDataPatient(null);
        setDataPatientLatestNotes(null);
      });
  };

  const getPatientEOC = async (patientId) => {
    await apiGet(
      `/eposides_of_care/${patientId}`,
      { headers: headers() },
      history
    )
      .then((response) => {
        if (!isNullOrEmpty(response.data) && response.data.length > 0) {
          setDataPatientEOC(response.data);
        } else {
          setDataPatientEOC(null);
        }
      })
      .catch((error) => {
        setDataPatientEOC(null);
      });
  };

  const getPatientObservations = async (patientId) => {
    await apiGet(
      `/observations/${patientId}/0`,
      { headers: headers() },
      history
    )
      .then((response) => {
        if (!isNullOrEmpty(response.data) && response.data.length > 0) {
          const sortedData = response.data.sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );

          setDataPatientObservations(sortedData);
        } else {
          setDataPatientObservations(null);
        }
      })
      .catch((error) => {
        console.error("getPatientObservations error :", error);
        setDataPatientObservations(null);
      });
  };

  const getPatientAssessments = async (patientId) => {
    await apiGet(
      `/assessments/${patientId}/all/0`,
      { headers: headers() },
      history
    )
      .then((response) => {
        if (!isNullOrEmpty(response.data) && response.data.length > 0) {
          const sortedData = response.data.sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );

          setDataPatientAssessments(sortedData);
        } else {
          setDataPatientAssessments(null);
        }
      })
      .catch((error) => {
        console.error("getPatientAssessments error :", error);
        setDataPatientAssessments(null);
      });
  };

  const isNullOrEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "null") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (typeof value === "number" && isNaN(value)) {
      return true;
    }

    return false;
  };

  const onCloseReport = () => {
    setIsReportVisible(false);
  };

  const onCloseDischargeReport = () => {
    setIsDischargeReportVisible(false);
  };

  return (
    <Dialog
      fullScreen
      open={isVisible}
      onClose={onCloseScreen}
      TransitionComponent={Transition}
      PaperProps={{
        style: {
          backgroundColor: "#FFF",
          boxShadow: "none",
        },
      }}
    >
      <AppBar sx={{ position: "relative" }} color="primary">
        <Toolbar>
          <IconButton
            data-test="patient-close-btn"
            edge="start"
            color="inherit"
            onClick={onCloseScreen}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          {dataPatient !== null && (
            <Typography
              data-test="patient-firstname-surname"
              sx={{ ml: 2, flex: 1 }}
              style={{ paddingLeft: 8 }}
              variant="h6"
              component="div"
            >
              {dataPatient.firstname + " " + dataPatient.surname}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      <Box style={{ padding: 15 }}>
        <Grid container spacing={2}>
          {isValidPatientId ? (
            loading ? (
              <Grid item xs={12}>
                <CircularProgress color="primary" />
              </Grid>
            ) : (
              <>
                {dataPatient !== null ? (
                  <>
                    {/* <Grid item xs={12}>
                      <Typography variant="h4">
                        {dataPatient.firstname + " " + dataPatient.surname}
                      </Typography>
                    </Grid> */}

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        //startIcon={<PrintIcon />}
                        style={{ marginRight: 10 }}
                        onClick={() => {
                          //window.print();
                          setIsReportVisible(true);
                        }}
                      >
                        Generate Patient Report
                      </Button>
                      {dataPatient.status === 1 && (
                        <Button
                          variant="contained"
                          color="primary"
                          //startIcon={<PrintIcon />}
                          style={{ marginRight: 10 }}
                          onClick={() => {
                            //window.print();
                            setIsDischargeReportVisible(true);
                          }}
                        >
                          Generate Discharge Report
                        </Button>
                      )}
                    </Grid>
                    <Grid item xs={12} style={{ paddingBottom: 0 }}>
                      <Typography variant="h6">Patient Details</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper style={{ padding: 20 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">
                              Firstname
                            </Typography>
                            <Typography>{dataPatient.firstname}</Typography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">Surname</Typography>
                            <Typography>{dataPatient.surname}</Typography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">Gender</Typography>
                            <Typography>
                              {dataPatient.gender === "m"
                                ? "Male"
                                : dataPatient.gender === "u"
                                ? "Unknown"
                                : "Female"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">DOB</Typography>
                            <Typography>
                              {moment(dataPatient.dob).format("DD/MM/YYYY")}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">MRN</Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.mrn)
                                ? dataPatient.mrn
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">Phone</Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.phone)
                                ? dataPatient.phone
                                : "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="subtitle2">Address</Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.address)
                                ? dataPatient.address
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2">
                              Admitted Note
                            </Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.note)
                                ? dataPatient.note
                                : "-"}
                            </Typography>
                          </Grid>
                          {/* <Grid item xs={12}>
                            <Typography variant="subtitle2">
                              Past Medical History
                            </Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.past_medical_history)
                                ? dataPatient.past_medical_history
                                : "-"}
                            </Typography>
                          </Grid> */}
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">
                              Emergency Contact Name
                            </Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.emergency_name)
                                ? dataPatient.emergency_name
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">
                              Emergency Contact Number
                            </Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.emergency_phone)
                                ? dataPatient.emergency_phone
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">
                              Emergency Contact Relationship
                            </Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.relationship)
                                ? dataPatient.relationship
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>

                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">Status</Typography>
                            <Typography>
                              {dataPatient.status === 0
                                ? "Admitted"
                                : dataPatient.status === 1
                                ? "Discharged"
                                : "Scheduled"}
                            </Typography>{" "}
                          </Grid>

                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">
                              {dataPatient.facility_id !== 999999
                                ? "Admission Date"
                                : "Scheduled Admission Date"}
                            </Typography>
                            <Typography>
                              {dataPatient.admitted_date !== null
                                ? moment(dataPatient.admitted_date).format(
                                    "DD/MM/YYYY"
                                  )
                                : "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">
                              Expected Discharge Date
                            </Typography>
                            <Typography>
                              {moment(
                                dataPatient.exptected_discharge_date
                              ).format("DD/MM/YYYY")}
                            </Typography>
                          </Grid>

                          {dataPatient.facility_id !== 999999 && (
                            <Grid item xs={12} md={2}>
                              <Typography variant="subtitle2">
                                Days Since Admission
                              </Typography>
                              <Typography>
                                {dataPatient.admitted_date !== null
                                  ? moment()
                                      .startOf("day")
                                      .diff(
                                        moment(dataPatient.admitted_date),
                                        "days"
                                      )
                                  : "-"}
                              </Typography>
                            </Grid>
                          )}

                          <Grid item xs={12} md={2}>
                            <Typography variant="subtitle2">
                              Consultant
                            </Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.consultant)
                                ? dataPatient.consultant
                                : "-"}
                            </Typography>
                          </Grid>

                          {dataPatient.status === 1 && (
                            <Grid item xs={12} md={2}>
                              <Typography variant="subtitle2">
                                Discharge Date
                              </Typography>
                              <Typography>
                                {moment(dataPatient.discharge_date).format(
                                  "DD/MM/YYYY"
                                )}
                              </Typography>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <Typography variant="subtitle2">
                              Discharge Note
                            </Typography>
                            <Typography>
                              {!isNullOrEmpty(dataPatient.discharge_note)
                                ? dataPatient.discharge_note
                                : "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {dataPatientObservations != null &&
                      dataPatient.facility_id !== 999999 &&
                      dataPatientObservations.length > 0 && (
                        <>
                          <Grid item xs={12} style={{ paddingBottom: 0 }}>
                            <Typography variant="h6">Observations</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Paper
                              style={{
                                flexGrow: 1,
                                padding: 16,
                                paddingBottom: 32,
                              }}
                            >
                              <Observations data={dataPatientObservations} />
                            </Paper>
                          </Grid>
                        </>
                      )}

                    {dataPatientAssessments != null &&
                      dataPatientAssessments.length > 0 && (
                        <>
                          <Grid item xs={12} style={{ paddingBottom: 0 }}>
                            <Typography variant="h6">Assessments</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Paper
                              style={{
                                flexGrow: 1,
                                padding: 16,
                                paddingBottom: 32,
                              }}
                            >
                              <Assessments data={dataPatientAssessments} />
                            </Paper>
                          </Grid>
                        </>
                      )}

                    {dataPatientEOC != null && dataPatientEOC.length > 0 && (
                      <>
                        <Grid item xs={12} style={{ paddingBottom: 0 }}>
                          <Typography variant="h6">Episodes Of Care</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Paper>
                            <EpisodesOfCare
                              headings={[
                                "Date",
                                "Event Type",
                                "Discharge Note",
                                "Discharge To",
                                "Facility",
                              ]}
                              data={dataPatientEOC}
                            />
                          </Paper>
                        </Grid>
                      </>
                    )}

                    {dataPatientLatestNotes != null &&
                      dataPatientLatestNotes.length > 0 && (
                        <>
                          <Grid item xs={12} style={{ paddingBottom: 0 }}>
                            <Typography variant="h6">Latest Notes</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Paper>
                              <Notes
                                headings={["Date", "Title", "Note"]}
                                data={dataPatientLatestNotes}
                              />
                            </Paper>
                          </Grid>
                        </>
                      )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      No records to display
                    </Typography>
                  </Grid>
                )}
              </>
            )
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1">
                No valid patient ID found in the URL.
              </Typography>
            </Grid>
          )}
        </Grid>
        {isReportVisible && (
          <Report
            isVisible={isReportVisible}
            onCloseReport={onCloseReport}
            dataPatient={dataPatient}
            dataPatientObservations={dataPatientObservations}
            dataPatientAssessments={dataPatientAssessments}
            dataPatientEOC={dataPatientEOC}
            dataPatientLatestNotes={dataPatientLatestNotes}
          />
        )}

        {isDischargeReportVisible && (
          <DischargeReport
            isVisible={isDischargeReportVisible}
            onCloseDischargeReport={onCloseDischargeReport}
            dataPatient={dataPatient}
            dataPatientEOC={dataPatientEOC}
            dataPatientObservations={dataPatientObservations}
          />
        )}
      </Box>
    </Dialog>
  );
}
